import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyBrianenoMode({ brand, today, Copy }: ModeRendererProps) {
  // Brian Eno gradient: ambient soundscape
  const enoGradient = (
    <defs>
      <linearGradient id="enoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1A237E" />
        <stop offset="50%" stopColor="#4A148C" />
        <stop offset="100%" stopColor="#006064" />
      </linearGradient>
    </defs>
  );

  // Isochronous grids: rhythmic temporal structure
  const isochronousGrids: JSX.Element[] = [];
  for (let i = 0; i < 18; i++) {
    const y = 100 + i * 52;
    const beat = i % 4 === 0;
    isochronousGrids.push(
      <line
        key={`iso-${i}`}
        x1={70}
        y1={y}
        x2={724}
        y2={y}
        stroke="url(#enoGrad)"
        strokeWidth={beat ? 2 : 1}
        opacity={beat ? 0.5 : 0.3}
      />
    );
  }

  // Resonance bands: harmonic overtones
  const resonanceBands: JSX.Element[] = [];
  for (let i = 0; i < 12; i++) {
    const y = 140 + i * 75;
    const points = [];
    const frequency = 1 + i * 0.5;
    for (let j = 0; j < 24; j++) {
      const x = 50 + j * 30;
      const wave = Math.sin(j * 0.5 * frequency) * (30 - i * 2);
      points.push(`${x},${y + wave}`);
    }
    resonanceBands.push(
      <polyline
        key={`res-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#enoGrad)"
        strokeWidth={1.5}
        opacity={0.3}
      />
    );
  }

  // Phase drift: slowly evolving patterns
  const phaseDrift: JSX.Element[] = [];
  for (let i = 0; i < 20; i++) {
    const radius = 70 + i * 15;
    const phase = i * 0.2;
    const segments = 24;
    const points = [];
    for (let j = 0; j < segments + 1; j++) {
      const angle = (j * Math.PI * 2) / segments + phase;
      const drift = Math.sin(j * 2 + phase) * 8;
      points.push(`${397 + (radius + drift) * Math.cos(angle)},${561.5 + (radius + drift) * Math.sin(angle)}`);
    }
    phaseDrift.push(
      <polyline
        key={`drift-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#enoGrad)"
        strokeWidth={1}
        opacity={0.25}
      />
    );
  }

  // Ambient nodes: generative system points
  const ambientNodes: JSX.Element[] = [];
  for (let i = 0; i < 16; i++) {
    const angle = (i * Math.PI * 2) / 16;
    const radius = 200;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    ambientNodes.push(
      <g key={`node-${i}`}>
        <circle cx={x} cy={y} r={18} fill="none" stroke="url(#enoGrad)" strokeWidth={2} opacity={0.4} />
        <circle cx={x} cy={y} r={10} fill="#4A148C" opacity={0.3} />
      </g>
    );
  }

  // Central generative core
  const generativeCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={90} fill="none" stroke="#006064" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={65} fill="none" stroke="#4A148C" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={40} fill="none" stroke="#1A237E" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={18}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        AMBIENT
      </text>
    </g>
  );

  return (
    <g>
      {enoGradient}
      {isochronousGrids}
      {resonanceBands}
      {phaseDrift}
      {ambientNodes}
      {generativeCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
